import { FC, ReactElement, SyntheticEvent, useState } from 'react'
import { useAuth } from 'src/contexts/auth.context'
import {
  loginRequest,
  registerRequest,
  sendResetPasswordLinkRequest,
  windowRedirectToBackend,
} from 'src/services/api.service'
import { LoginForm, LoginFormValues } from './login-form'
import { RegisterForm, RegisterFormValues } from './register-form'
import { ForgotPasswordForm, ForgotPasswordFormValues } from './forgot-password-form'
import { ConfirmationLinkSentWithoutRouter } from './confirmation-link-sent-without-router'

export const AuthHome: FC = (): ReactElement => {
  const { setUser, setToken } = useAuth()
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [form, setForm] = useState('login')

  const handleLoginLink = (e: SyntheticEvent) => {
    e.preventDefault()
    setForm('login')
  }

  const handleSignupLink = (e: SyntheticEvent) => {
    e.preventDefault()
    setForm('register')
  }

  const handleForgotPasswordLink = (e: SyntheticEvent) => {
    e.preventDefault()
    setForm('forgot')
  }

  const handleLoginSubmit = async (values: LoginFormValues) => {
    try {
      setIsLoading(true)
      setError('')
      const response = await loginRequest(values)
      const { status, data: { user, token }} = response;

      if (status === 200) {
        setUser(user)
        setToken(token)
        windowRedirectToBackend(token, process.env.REACT_APP_SELF_HOST ?? '')
      }
    } catch (err) {
      setError(err.response.data?.error[0])
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegisterSubmit = async (values: RegisterFormValues) => {
    try {
      setIsLoading(true)
      setError('')
      await registerRequest(values)
      setEmail(values.email)
      setForm('confirmation-link-sent')
    } catch (err) {
      setError(err?.response.data?.error[0])
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPasswordSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      setIsLoading(true)
      setError('')
      await sendResetPasswordLinkRequest(values)
      setEmail(values.email)
      setForm('confirmation-link-sent')
    } catch (err) {
      setError(err?.response.data?.error[0])
    } finally {
      setIsLoading(false)
    }
  }

  if (form === 'login') {
    return (
      <LoginForm
        isLoading={isLoading}
        isLanding
        error={error}
        handleSubmit={handleLoginSubmit}
        handleSignupLink={handleSignupLink}
        handleForgotPasswordLink={handleForgotPasswordLink}
      />
    )
  }

  if (form === 'register') {
    return (
      <RegisterForm
        isLoading={isLoading}
        isLanding
        error={error}
        handleSubmit={handleRegisterSubmit}
        handleLoginLink={handleLoginLink}
      />
    )
  }

  if (form === 'forgot') {
    return (
      <ForgotPasswordForm
        error={error}
        isLanding
        isLoading={isLoading}
        handleSubmit={handleForgotPasswordSubmit}
        handleLoginLink={handleLoginLink}
        handleSignupLink={handleSignupLink}
      />
    )
  }

  if (form === 'confirmation-link-sent') {
    return <ConfirmationLinkSentWithoutRouter email={email} />
  }

  return <></>
}
